import React, { createContext, useContext, useEffect, useState, useRef, useMemo, ReactNode } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../hooks/Keycloak/useAuthenticatedFetchWithWorkspace";
import { ReferentialItem } from "../types/Customer";

interface ClientsDataContextType {
    identifierTypes: ReferentialItem[];
    personAttributes: ReferentialItem[];
    loading: boolean;
    error: string | null;
}

const ClientsDataContext = createContext<ClientsDataContextType | undefined>(undefined);

export const useClientsData = (): ClientsDataContextType => {
    const context = useContext(ClientsDataContext);
    if (!context) throw new Error("useClientsData must be used within ClientsDataProvider");
    return context;
};

const toList = (result: unknown): ReferentialItem[] => {
    const r = result as Record<string, unknown>;
    const raw = Array.isArray(result) ? result
        : Array.isArray(r?.["hydra:member"]) ? r["hydra:member"]
        : Array.isArray(r?.member) ? r.member
        : Array.isArray(r?.data) ? r.data
        : [];
    return raw as ReferentialItem[];
};

export const ClientsDataProvider = ({ children }: { children: ReactNode }) => {
    const { authFetch, isReady, isLoading: workspaceLoading, workspaceId } = useAuthenticatedFetchWithWorkspace();

    const [identifierTypes, setIdentifierTypes] = useState<ReferentialItem[]>([]);
    const [personAttributes, setPersonAttributes] = useState<ReferentialItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadedForWorkspace = useRef<string | null>(null);

    useEffect(() => {
        if (workspaceLoading || !isReady || !workspaceId) {
            if (!identifierTypes.length) setLoading(true);
            return;
        }

        if (loadedForWorkspace.current === workspaceId) {
            return;
        }

        if (loadedForWorkspace.current !== null) {
            setIdentifierTypes([]);
            setPersonAttributes([]);
        }
        setLoading(true);
        setError(null);

        Promise.all([
            authFetch("/per_person_identifier_types?activeSearch=1&excluded=0", { method: "GET" }),
            authFetch("/per_attributes", { method: "GET" }),
        ])
            .then(([typesResult, attrsResult]) => {
                setIdentifierTypes(toList(typesResult));
                setPersonAttributes(toList(attrsResult).filter((item) => item.id !== 3 && item.id !== "3"));
                loadedForWorkspace.current = workspaceId;
            })
            .catch((err: unknown) => setError(err instanceof Error ? err.message : "Erreur de chargement"))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, workspaceLoading, workspaceId]);

    const contextValue = useMemo(
        () => ({ identifierTypes, personAttributes, loading, error }),
        [identifierTypes, personAttributes, loading, error]
    );

    return (
        <ClientsDataContext.Provider value={contextValue}>
            {children}
        </ClientsDataContext.Provider>
    );
};
